import type {
  ContactRecord,
  CustomerCollectionRecord,
  CustomerCategoryRecord,
  CustomerContractStatus,
  CustomerDetailRecord,
  CustomerInvoiceStatus,
  CustomerRecord,
  CustomerStatus,
  CustomerType,
  ProjectStatus
} from '@shared/types';
import { normalizeBusinessIconName, type BusinessIconName } from '@renderer/shared/components/icons';
import type { SearchableSelectOption } from '@renderer/shared/components/primitives';

type SortDirection = 'asc' | 'desc';

export interface CustomerTableRowViewModel {
  id: string;
  name: string;
  statusLabel: string;
  statusTone: 'success' | 'warning' | 'neutral';
  typeLabel: string;
  typeTone: 'primary' | 'warning' | 'info';
  categoryLabel: string;
  categoryIconName: BusinessIconName;
  categoryArchived: boolean;
  contactName: string;
  phone: string | null;
  phoneLabel: string;
}

export interface CustomerEditorViewModel {
  title: string;
  favoriteLabel: string | null;
  statusLabel: string;
  statusTone: 'success' | 'warning' | 'neutral';
  typeLabel: string;
  typeTone: 'primary' | 'warning' | 'info';
  categoryLabel: string;
  categoryIconName: BusinessIconName;
  categoryArchived: boolean;
  ratingLabel: string | null;
  siteLinks: Array<{
    key: 'live' | 'local';
    icon: 'website' | 'monitor';
    label: string;
    value: string | null;
  }>;
  activeSinceTitle: string;
  completedProjectsLabel: string;
  generatedRevenueLabel: string;
  primaryContact: {
    name: string;
    role: string | null;
    email: string | null;
    phone: string | null;
    whatsapp: string | null;
  } | null;
  primaryAddress: string | null;
  featuredNote: string | null;
  projectItems: Array<{
    id: string;
    name: string;
    statusLabel: string;
    statusTone: 'primary' | 'info' | 'warning' | 'success' | 'neutral';
    progressPercent: number | null;
    updatedLabel: string;
    domainLabel: string | null;
  }>;
  contractItems: Array<{
    id: string;
    title: string;
    statusLabel: string;
    statusTone: 'warning' | 'primary' | 'success' | 'neutral';
    metaLabel: string;
    amountLabel: string;
  }>;
  invoiceItems: Array<{
    id: string;
    label: string;
    statusLabel: string;
    statusTone: 'warning' | 'primary' | 'success' | 'neutral';
    metaLabel: string;
    amountLabel: string;
  }>;
}

export interface CustomerSelectionItemViewModel {
  id: string;
  label: string;
  subtitle: string | null;
}

function getPrimaryContact(customer: CustomerRecord, contacts: ContactRecord[]): ContactRecord | null {
  return contacts.find((contact) => contact.id === customer.primaryContactId) ?? contacts[0] ?? null;
}

function getCustomerCategory(categories: CustomerCategoryRecord[], customerCategoryId: string | null): CustomerCategoryRecord | null {
  return customerCategoryId ? categories.find((category) => category.id === customerCategoryId) ?? null : null;
}

function customerStatusTone(status: CustomerStatus): CustomerEditorViewModel['statusTone'] {
  switch (status) {
    case 'active':
      return 'success';
    case 'lead':
      return 'warning';
    default:
      return 'neutral';
  }
}

function customerTypeTone(type: CustomerType): CustomerTableRowViewModel['typeTone'] {
  switch (type) {
    case 'company':
      return 'primary';
    case 'sole_trader':
      return 'warning';
    default:
      return 'info';
  }
}

function projectStatusTone(status: ProjectStatus): CustomerEditorViewModel['projectItems'][number]['statusTone'] {
  switch (status) {
    case 'active':
      return 'primary';
    case 'in_progress':
      return 'info';
    case 'pending':
      return 'warning';
    case 'complete':
      return 'success';
    default:
      return 'neutral';
  }
}

function contractStatusTone(status: CustomerContractStatus): CustomerEditorViewModel['contractItems'][number]['statusTone'] {
  switch (status) {
    case 'active':
      return 'primary';
    case 'signed':
      return 'success';
    case 'draft':
      return 'warning';
    default:
      return 'neutral';
  }
}

function invoiceStatusTone(status: CustomerInvoiceStatus): CustomerEditorViewModel['invoiceItems'][number]['statusTone'] {
  switch (status) {
    case 'issued':
      return 'primary';
    case 'paid':
      return 'success';
    case 'overdue':
      return 'warning';
    default:
      return 'neutral';
  }
}

function withDirection(value: number, direction: SortDirection): number {
  return direction === 'asc' ? value : value * -1;
}

function sortCustomers(customers: CustomerRecord[], sort: 'recent' | 'name', direction: SortDirection): CustomerRecord[] {
  const nextCustomers = [...customers];

  if (sort === 'name') {
    return nextCustomers.sort((left, right) => withDirection(left.name.localeCompare(right.name), direction));
  }

  return nextCustomers.sort((left, right) => withDirection(new Date(left.updatedAt).getTime() - new Date(right.updatedAt).getTime(), direction));
}

function formatShortDate(locale: string, value: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short'
  }).format(new Date(value));
}

function formatMonthYear(locale: string, value: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric'
  }).format(new Date(value));
}

function formatCurrency(locale: string, value: number, currency: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
  }).format(value);
}

function buildAddressLine(detail: CustomerDetailRecord): string | null {
  const address =
    detail.addresses.find((item) => item.id === detail.customer.primaryAddressId) ?? detail.addresses.find((item) => item.isPrimary) ?? null;

  if (!address) {
    return null;
  }

  return [address.streetLine, address.city, address.county].filter(Boolean).join(', ');
}

export function selectCustomersSummary(collection: CustomerCollectionRecord) {
  return {
    activeCount: collection.customers.filter((customer) => customer.status !== 'archived').length,
    archivedCount: collection.customers.filter((customer) => customer.status === 'archived').length
  };
}

export function selectCustomerTypeOptions(t: (key: string) => string) {
  return [
    { value: 'all', label: t('customers.filters.allTypes') },
    { value: 'company', label: t('enums.customerType.company') },
    { value: 'sole_trader', label: t('enums.customerType.sole_trader') },
    { value: 'individual', label: t('enums.customerType.individual') }
  ];
}

export function selectCustomerCategoryOptions(
  collection: CustomerCollectionRecord,
  t: (key: string) => string
): SearchableSelectOption[] {
  return [
    { value: 'all', label: t('customers.filters.allCategories'), icon: 'filter' },
    { value: 'unassigned', label: t('customers.filters.unassignedCategory'), icon: 'times' },
    ...collection.customerCategories.map((category) => ({
      value: category.id,
      label: category.name,
      icon: normalizeBusinessIconName(category.iconName),
      description: category.archivedAt ? t('common.archived') : undefined
    }))
  ];
}

export function selectCustomerRows(
  collection: CustomerCollectionRecord,
  options: {
    statusFilter: 'all' | CustomerStatus;
    categoryFilter: 'all' | 'unassigned' | string;
    sort: 'recent' | 'name';
    sortDirection?: SortDirection | undefined;
    t: (key: string) => string;
  }
): CustomerTableRowViewModel[] {
  return sortCustomers(
    collection.customers.filter((customer) => {
      const matchesStatus = options.statusFilter === 'all' || customer.status === options.statusFilter;
      const matchesCategory =
        options.categoryFilter === 'all' ||
        (options.categoryFilter === 'unassigned' && !customer.customerCategoryId) ||
        customer.customerCategoryId === options.categoryFilter;
      return matchesStatus && matchesCategory;
    }),
    options.sort,
    options.sortDirection ?? (options.sort === 'name' ? 'asc' : 'desc')
  ).map((customer) => {
    const primaryContact = getPrimaryContact(customer, collection.contacts.filter((contact) => contact.customerId === customer.id));
    const category = getCustomerCategory(collection.customerCategories, customer.customerCategoryId);

    return {
      id: customer.id,
      name: customer.name,
      statusLabel: options.t(`enums.customerStatus.${customer.status}`),
      statusTone: customerStatusTone(customer.status),
      typeLabel: options.t(`enums.customerType.${customer.type}`),
      typeTone: customerTypeTone(customer.type),
      categoryLabel: category?.name ?? options.t('customers.filters.unassignedCategory'),
      categoryIconName: normalizeBusinessIconName(category?.iconName),
      categoryArchived: Boolean(category?.archivedAt),
      contactName: primaryContact?.name ?? options.t('customers.table.noContact'),
      phone: primaryContact?.phone ?? null,
      phoneLabel: primaryContact?.phone ?? options.t('customers.table.noPhone')
    };
  });
}

export function selectCustomerEditorModel(
  detail: CustomerDetailRecord,
  options: {
    locale: string;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): CustomerEditorViewModel {
  const primaryContact = getPrimaryContact(detail.customer, detail.contacts);
  const featuredNote = detail.notes.find((note) => note.pinned) ?? detail.notes[0] ?? null;
  const category = getCustomerCategory(detail.customerCategories, detail.customer.customerCategoryId);

  return {
    title: detail.customer.name,
    favoriteLabel: detail.overview.isFavorite ? options.t('customers.editor.hero.favorite') : null,
    statusLabel: options.t(`enums.customerStatus.${detail.customer.status}`),
    statusTone: customerStatusTone(detail.customer.status),
    typeLabel: options.t(`enums.customerType.${detail.customer.type}`),
    typeTone: customerTypeTone(detail.customer.type),
    categoryLabel: category?.name ?? options.t('customers.filters.unassignedCategory'),
    categoryIconName: normalizeBusinessIconName(category?.iconName),
    categoryArchived: Boolean(category?.archivedAt),
    ratingLabel: detail.overview.rating !== null ? detail.overview.rating.toFixed(1) : null,
    siteLinks: [
      {
        key: 'live',
        icon: 'website',
        label: options.t('customers.editor.site.live'),
        value: detail.overview.liveSiteUrl
      },
      {
        key: 'local',
        icon: 'monitor',
        label: options.t('customers.editor.site.local'),
        value: detail.overview.localSiteUrl
      }
    ],
    activeSinceTitle: detail.overview.activeSince
      ? options.t('customers.editor.summary.activeSince', {
          value: formatMonthYear(options.locale, detail.overview.activeSince)
        })
      : options.t('customers.editor.summary.activeSinceUnknown'),
    completedProjectsLabel: options.t('customers.editor.summary.completedProjects', {
      count: detail.overview.completedProjectsCount
    }),
    generatedRevenueLabel: formatCurrency(options.locale, detail.overview.generatedRevenue, detail.overview.currency),
    primaryContact: primaryContact
      ? {
          name: primaryContact.name,
          role: primaryContact.role,
          email: primaryContact.email,
          phone: primaryContact.phone,
          whatsapp: primaryContact.whatsapp
        }
      : null,
    primaryAddress: buildAddressLine(detail),
    featuredNote: featuredNote?.body ?? null,
    projectItems: detail.projectSummaries.map((project) => ({
      id: project.id,
      name: project.name,
      statusLabel: options.t(`enums.projectStatus.${project.status}`),
      statusTone: projectStatusTone(project.status),
      progressPercent: project.progressPercent,
      updatedLabel: options.t('customers.editor.projects.updated', {
        date: formatShortDate(options.locale, project.updatedAt)
      }),
      domainLabel: project.primaryDomain ?? project.localDomain
    })),
    contractItems: detail.contractSummaries.map((contract) => ({
      id: contract.id,
      title: contract.title,
      statusLabel: options.t(`enums.customerContractStatus.${contract.status}`),
      statusTone: contractStatusTone(contract.status),
      metaLabel: contract.effectiveUntil
        ? options.t('customers.editor.contracts.expires', {
            date: formatShortDate(options.locale, contract.effectiveUntil)
          })
        : options.t('customers.editor.contracts.noEndDate'),
      amountLabel:
        contract.totalValue !== null ? formatCurrency(options.locale, contract.totalValue, contract.currency) : options.t('common.notSet')
    })),
    invoiceItems: detail.invoiceSummaries.map((invoice) => ({
      id: invoice.id,
      label: invoice.label,
      statusLabel: options.t(`enums.customerInvoiceStatus.${invoice.status}`),
      statusTone: invoiceStatusTone(invoice.status),
      metaLabel: invoice.dueDate
        ? options.t('customers.editor.invoices.due', {
            date: formatShortDate(options.locale, invoice.dueDate)
          })
        : options.t('common.noDeadline'),
      amountLabel: formatCurrency(options.locale, invoice.grandTotal, invoice.currency)
    }))
  };
}

export function selectCustomerSelectionItems(collection: CustomerCollectionRecord): CustomerSelectionItemViewModel[] {
  return collection.customers.map((customer) => ({
    id: customer.id,
    label: customer.name,
    subtitle: customer.legalName
  }));
}
